import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TrendingUp, TrendingDown } from 'lucide-react'
import type { PeriodMetrics } from '@/types/attendance-metrics'

interface PeriodComparisonCardProps {
  current: PeriodMetrics
  previous: PeriodMetrics
  periodLabel: string
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

function calculateDelta(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null
  return ((current - previous) / previous) * 100
}

function formatDelta(delta: number | null, lowerIsBetter: boolean = false): {
  text: string
  isPositive: boolean
} {
  if (delta === null) return { text: 'N/A', isPositive: false }

  const sign = delta >= 0 ? '+' : ''
  const text = `${sign}${delta.toFixed(1)}%`

  // For FRT/TMR, lower is better (negative delta is good)
  // For counts, higher is better (positive delta is good)
  const isPositive = lowerIsBetter ? delta < 0 : delta > 0

  return { text, isPositive }
}

export function PeriodComparisonCard({ current, previous, periodLabel }: PeriodComparisonCardProps) {
  const frtDelta = calculateDelta(current.frtAvg, previous.frtAvg)
  const tmrDelta = calculateDelta(current.tmrAvg, previous.tmrAvg)
  const messagesDelta = calculateDelta(current.messageCount, previous.messageCount)
  const responsesDelta = calculateDelta(current.responseCount, previous.responseCount)
  const ticketsDelta = calculateDelta(current.ticketCount, previous.ticketCount)

  const metrics = [
    {
      label: 'FRT Médio',
      current: formatTime(current.frtAvg),
      previous: formatTime(previous.frtAvg),
      delta: formatDelta(frtDelta, true),
    },
    {
      label: 'TMR Médio',
      current: formatTime(current.tmrAvg),
      previous: formatTime(previous.tmrAvg),
      delta: formatDelta(tmrDelta, true),
    },
    {
      label: 'Mensagens',
      current: current.messageCount.toLocaleString(),
      previous: previous.messageCount.toLocaleString(),
      delta: formatDelta(messagesDelta, false),
    },
    {
      label: 'Respostas',
      current: current.responseCount.toLocaleString(),
      previous: previous.responseCount.toLocaleString(),
      delta: formatDelta(responsesDelta, false),
    },
    {
      label: 'Tickets',
      current: current.ticketCount.toLocaleString(),
      previous: previous.ticketCount.toLocaleString(),
      delta: formatDelta(ticketsDelta, false),
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comparação de Períodos</CardTitle>
        <CardDescription>{periodLabel}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {metrics.map((metric) => (
            <div key={metric.label} className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium">{metric.label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    Anterior: {metric.previous}
                  </span>
                  <span className="text-xs text-muted-foreground">→</span>
                  <span className="text-xs font-semibold">Atual: {metric.current}</span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {metric.delta.isPositive ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-600" />
                )}
                <Badge
                  variant="outline"
                  className={
                    metric.delta.isPositive
                      ? 'border-green-600 text-green-600'
                      : 'border-red-600 text-red-600'
                  }
                >
                  {metric.delta.text}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
